import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Calendar, Copy, Check, ShieldAlert, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const DEFAULT_FUNCTION_BASE = 'https://wgqfslalqgnnxryezuxg.supabase.co/functions/v1';

const CalendarFeedLink = () => {
    const [token, setToken] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    const functionBase =
        import.meta.env?.VITE_SUPABASE_FUNCTIONS_URL ||
        DEFAULT_FUNCTION_BASE;

    const prodUrl = token
        ? `${functionBase}/admin-appointment-ics?token=${token}`
        : '';

    useEffect(() => {
        fetchOrCreateToken();
    }, []);

    const fetchOrCreateToken = async () => {
        setLoading(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('admins')
                .select('calendar_token')
                .eq('id', user.id)
                .single();

            if (error) throw error;

            if (data?.calendar_token) {
                setToken(data.calendar_token);
                return;
            }

            const newToken = crypto.randomUUID().replaceAll('-', '');

            const { error: updateError } = await supabase
                .from('admins')
                .update({ calendar_token: newToken })
                .eq('id', user.id);

            if (updateError) throw updateError;

            setToken(newToken);
        } catch (error) {
            toast({
                title: 'Calendar Link Error',
                description: error.message,
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!prodUrl) return;

        await navigator.clipboard.writeText(prodUrl);
        setCopied(true);
        toast({
            title: 'Link Copied',
            description: 'iCal feed URL copied to clipboard.'
        });
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <>
            <Button
                variant="outline"
                onClick={() => setIsOpen(true)}
                className="h-11 border-slate-200 bg-white hover:bg-slate-50 font-bold gap-2 text-xs text-slate-900"
            >
                <Calendar size={16} className="text-blue-600" />
                SYNC CALENDAR
            </Button>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-[560px] bg-white text-slate-950 border-slate-200 rounded-[28px] p-8 shadow-2xl">
                    <DialogHeader>
                        <div className="flex items-center gap-2 text-blue-600 font-black uppercase tracking-widest text-[10px] mb-2">
                            <Calendar size={14} /> External Synchronization
                        </div>
                        <DialogTitle className="text-3xl font-black tracking-tight">
                            Meet & Greet Calendar Feed
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 font-medium">
                            Add this private iCal feed to Google, Outlook, or Apple Calendar to receive booked candidate appointments automatically.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-6">
                        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 flex gap-4">
                            <ShieldAlert className="text-amber-600 shrink-0" size={20} />
                            <p className="text-[11px] font-bold text-amber-900 leading-relaxed uppercase tracking-tight">
                                This is a private URL. Anyone with this link can view scheduled recruitment appointments for this calendar feed.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">
                                Your iCal Feed URL
                            </label>
                            <div className="flex gap-2">
                                <Input
                                    readOnly
                                    value={loading ? 'Generating calendar link...' : prodUrl}
                                    className="bg-slate-50 h-12 rounded-2xl border-slate-200 font-mono text-[10px] text-slate-700"
                                />
                                <Button
                                    onClick={handleCopy}
                                    disabled={loading || !prodUrl}
                                    className="h-12 w-12 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                                >
                                    {loading ? <Loader2 className="animate-spin" size={18} /> : copied ? <Check size={18} /> : <Copy size={18} />}
                                </Button>
                            </div>
                        </div>

                        <div className="space-y-3 pt-4 border-t border-slate-100">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                How to set up
                            </p>
                            <ol className="text-xs space-y-2 text-slate-700 font-medium list-decimal ml-4">
                                <li>Open Google Calendar on desktop.</li>
                                <li>Click the plus next to Other calendars.</li>
                                <li>Select From URL.</li>
                                <li>Paste this link and click Add calendar.</li>
                            </ol>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            onClick={() => setIsOpen(false)}
                            className="w-full h-12 rounded-2xl bg-slate-950 hover:bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest"
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CalendarFeedLink;
